<template>
  <g-button class="mx-2" label="Export" icon="get_app" :loading="loading" @onclick="exportData" />
</template>

<script>
import PhoneService from "@/components/form/phones/phoneService";
import PHONE_MASK from "@/config/PHONE_MASK";
import { ExportService } from "@/modules/exports";

export default {
  props: {
    searchQuery: Object,
  },
  data() {
    return {
      loading: false,
    };
  },
  methods: {
    exportData() {
      this.loading = true;

      return this.$store
        .dispatch("DatabaseStore/getClientNames", {
          query: this.searchQuery,
        })
        .then((rows) => {
          const fileName = `clients-${new Date().getTime()}.xlsx`;
          const exportRows = [
            [],
            [],
            ["Client name", "Title", "Name", "Email", "Phone", "Full address"],
            ...rows.map((client) => {
              const contact = this.primaryContact(client);
              return [
                this.clean(client.name),
                this.clean(contact?.title ?? ""),
                this.clean(contact?.name ?? ""),
                this.clean(contact?.email ?? ""),
                this.clean(this.phoneNumber(contact?.phone_number ?? "")),
                this.clean(this.fullAddress(client)),
              ];
            }),
          ];

          return ExportService({
            fileName,
            rows: exportRows,
            iconArea: "A1:B1",
            firstRowHeight: 90,
            landscape: true,
            printArea: `A1:F${exportRows.length}`,
            config: this.exportConfig(exportRows.length),
          });
        })
        .catch((err) => {
          this.$root.$error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportConfig(rowCount) {
      const headerFill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE8F3DC" },
      };
      const border = {
        top: { style: "thin", color: { argb: "FFB7B7B7" } },
        left: { style: "thin", color: { argb: "FFB7B7B7" } },
        bottom: { style: "thin", color: { argb: "FFB7B7B7" } },
        right: { style: "thin", color: { argb: "FFB7B7B7" } },
      };

      const config = [
        { row: 0, column: 1, options: { width: 30 } },
        { row: 0, column: 2, options: { width: 18 } },
        { row: 0, column: 3, options: { width: 26 } },
        { row: 0, column: 4, options: { width: 32 } },
        { row: 0, column: 5, options: { width: 22 } },
        { row: 0, column: 6, options: { width: 48 } },
        {
          row: 3,
          options: {
            font: { bold: true },
            fill: headerFill,
            border,
            alignment: { vertical: "middle" },
            height: 24,
          },
        },
      ];

      for (let row = 4; row <= rowCount; row++) {
        config.push({
          row,
          options: {
            border,
            alignment: { vertical: "top", wrapText: true },
          },
        });
      }

      return config;
    },
    phoneNumber(phoneNumber) {
      const phone = PhoneService.phoneDisplay(phoneNumber, PHONE_MASK("US")[0], "US");
      if (!phone) return "";
      return [phone.code ? `+${phone.code}` : "", phone.phone, phone.ext ? `ext. ${phone.ext}` : ""]
        .filter(Boolean)
        .join(" ");
    },
    fullAddress(client) {
      const address = this.parseAddress(client.address);

      return [
        address.address || client.address_line,
        address.city || client.city,
        address.subjur || client.subjur,
        address.country || client.country,
        address.zip_code || client.zip_code,
      ]
        .map((part) => (part ? `${part}`.replace(/\s+/g, " ").trim() : ""))
        .filter(Boolean)
        .join(", ");
    },
    parseAddress(address) {
      if (!address) return {};
      if (typeof address === "object") return address;

      try {
        return JSON.parse(address);
      } catch (err) {
        return { address };
      }
    },
    clean(value) {
      if (!value) return "";
      return `${value}`.trim();
    },
    primaryContact(client) {
      return (
        client?.contacts?.find((contact) => contact.primary_contact) ??
        client?.contacts?.find((contact) => contact.id === client.primary_contact_id) ??
        client?.contacts?.[0]
      );
    },
  },
};
</script>
